import React, { useRef, useState, useEffect, useCallback } from 'react';
import Photo from './photo';
import classNames from 'classnames';
import I18n from 'i18n-js';
import { get } from 'lodash';
import { camelizeKeys } from 'humps';

interface IProps {
  uploadOptions: any;
  onFileAdded: Function;
  onUploadFailure?: Function;
  onDragStart: Function;
  onDragEnd: Function;
  dropZoneSelector?: string;
  thumbMessage?: string;
}

interface ICloudinaryResponse {
  public_id: string;
  version: number;
  width: number;
  height: number;
  format: string;
  resource_type: string;
}

const defaultDropZoneSelector = 'body';

const Uploader: React.FC<IProps> = ({
  uploadOptions,
  onFileAdded,
  onUploadFailure = () => {},
  onDragStart,
  onDragEnd,
  dropZoneSelector = defaultDropZoneSelector,
  thumbMessage,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  /**
    isDragging was previously managed as state. Copilot suggested
    using a ref instead of state to avoid a bug with this code:

    // dragleave fires
    setIsDragging(false);  // State is false
    setTimeout(() => {
      if (!isDragging) {  // This captures isDragging = false
        // dragover fired in the meantime and set state to true
        // But this still sees false!
        handleDragLeave();  // Incorrectly calls this
      }
    }, 10);
  */
  const isDraggingRef = useRef(false);

  const handleDragLeave = useCallback(() => {
    isDraggingRef.current = false;
    onDragEnd();
  }, [onDragEnd]);

  const handleUploadStart = useCallback(() => {
    setIsUploading(true);
    setProgress(0);
    handleDragLeave();
  }, [handleDragLeave]);

  const handleUploadDone = useCallback(() => {
    setIsUploading(false);
  }, []);

  const handleUploadSuccess = useCallback((result: ICloudinaryResponse) => {
    const camelized = camelizeKeys(result);
    onFileAdded(new Photo(camelized));
    handleUploadDone();
  }, [onFileAdded, handleUploadDone]);

  const handleProgress = useCallback((data) => {
    const newProgress = data.loaded / data.total * 100;
    setProgress(newProgress);
  }, []);

  const handleDragOver = useCallback(() => {
    isDraggingRef.current = true;
    onDragStart();
  }, [onDragStart]);

  const bindDragLeave = useCallback(() => {
    $(document).on('dragleave', dropZoneSelector, () => {
      // Set isDragging to false. If it is still false in 10ms, then we have actually
      // left the window. Otherwise this may be called when dragging into other elements
      // on the page, in which case isDragging will have been set back to true.
      isDraggingRef.current = false;
      setTimeout(
        () => {
          if (!isDraggingRef.current) {
            handleDragLeave();
          }
        },
        10,
      );
    });
  }, [dropZoneSelector, handleDragLeave]);

  useEffect(() => {
    const $dropZone = $(dropZoneSelector);
    const options = {
      url: get(uploadOptions, 'html.data.url'),
      formData: get(uploadOptions, 'html.data.form_data'),
      dataType: 'json',
      paramName: 'file',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      sequentialUploads: true,
      acceptFileTypes: /^image\/jpeg|image\/png|image\/heic|image\/heif|image\/webp|application\/pdf$/i,
      done: (_e, data) => handleUploadSuccess(data.result),
      fail: onUploadFailure,
      always: handleUploadDone,
      send: handleUploadStart,
      progressall: (_e, data) => handleProgress(data),
      dropZone: $dropZone,
      dragover: handleDragOver,
    };

    bindDragLeave();
    ($(inputRef.current) as any).fileupload(options);

    return () => {
      $(document).off('dragleave');
    };
  }, [
    uploadOptions,
    dropZoneSelector,
    onUploadFailure,
    handleUploadSuccess,
    handleUploadDone,
    handleUploadStart,
    handleProgress,
    handleDragOver,
    bindDragLeave,
  ]);

  const renderLabel = () => {
    if (isUploading) {
      const percent = progress.toFixed(0);
      return I18n.t('discovery.photos.uploader.progress', { percent });
    }
    return thumbMessage || I18n.t('discovery.photos.uploader.uploadPhotos');
  };

  const className = classNames(
    'img-uploader__thumb',
    'img-uploader__thumb--add-photo',
    { 'img-uploader__thumb--add-photo--active': !isUploading },
  );

  return (
    <div className={className}>
      <div className="img-uploader__thumb__message">
        <label>{renderLabel()}</label>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/heic,image/heif,image/webp,application/pdf"
        multiple={get(uploadOptions, 'html.multiple')}
      />
    </div>
  );
};

export default Uploader;
